/**
 * CompanyDocumentsPage Component
 * 
 * Página de gerenciamento de documentos da empresa.
 * 
 * Funcionalidades:
 * - Lista de documentos da empresa
 * - Filtros por categoria
 * - Upload de novos documentos
 * - Edição de documentos
 * - Visualização de documentos
 * - Alertas de documentos prestes a vencer
 */

import { useState, useEffect } from 'react'
import { Plus } from 'lucide-react'
import { companyDocumentService, documentCategoryService } from '../../../services/documentsApi'
import { DocumentList, DocumentExpiryAlert, CompanyDocumentModal } from '../../../components/Documents'
import ConfirmModal from '../../../components/ERP/ConfirmModal/ConfirmModal'
import './documentsShell.css'
import './CompanyDocumentsPage.css'

function CompanyDocumentsPage() {
  const [documents, setDocuments] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingDocument, setEditingDocument] = useState(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [documentToDelete, setDocumentToDelete] = useState(null)
  const [filters, setFilters] = useState({
    category_id: '',
    expiry_status: ''
  })
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [expiringDocuments, setExpiringDocuments] = useState([])
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  // Carregar categorias apenas uma vez ao montar o componente
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await documentCategoryService.getAll({ type: 'company' })
        if (response.success) {
          // A API retorna { success: true, data: { categories: [...] } }
          const categoriesData = response.data?.categories || 
                                (Array.isArray(response.data) ? response.data : [])
          setCategories(categoriesData)
        } else {
          setCategories([])
        }
      } catch (error) {
        console.error('Erro ao carregar categorias:', error)
        setCategories([])
      }
    }
    loadCategories()
  }, [])

  // Carregar documentos quando currentPage ou propriedades de filters mudarem
  useEffect(() => {
    const loadDocuments = async () => {
      setLoading(true)
      try {
        const params = {
          page: currentPage,
          limit: 12,
          ...filters
        }

        const response = await companyDocumentService.getAll(params)
        
        if (response.success) {
          setDocuments(response.data?.documents || response.data || [])
          setTotalPages(response.data?.totalPages || 1)
          setTotalItems(response.data?.totalItems || response.data?.length || 0)
          
          // Separar documentos expirando
          const expiring = (response.data?.documents || response.data || [])
            .filter(doc => {
              if (!doc.expiry_date) return false
              const expiryDate = new Date(doc.expiry_date)
              const today = new Date()
              const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24))
              return daysUntilExpiry <= 30 && daysUntilExpiry >= 0
            })
          setExpiringDocuments(expiring)
        }
      } catch (error) {
        console.error('Erro ao carregar documentos:', error)
      } finally {
        setLoading(false)
      }
    }
    loadDocuments()
  }, [currentPage, filters, refreshTrigger])

  const handleUpload = async (data, documentId, file) => {
    try {
      let response
      if (documentId) {
        // Edição
        if (file) {
          // Com novo arquivo
          response = await companyDocumentService.upload(file, data)
        } else {
          // Apenas atualizar dados
          response = await companyDocumentService.update(documentId, data)
        }
      } else {
        // Criação
        if (!file) {
          throw new Error('Arquivo é obrigatório para criar documento')
        }
        response = await companyDocumentService.upload(file, data)
      }

      if (response.success) {
        setModalOpen(false)
        setEditingDocument(null)
        setRefreshTrigger(prev => prev + 1) // Forçar reload
      } else {
        alert(response.message || 'Erro ao salvar documento')
      }
    } catch (error) {
      console.error('Erro ao salvar documento:', error)
      alert(error.response?.data?.message || 'Erro ao salvar documento')
    }
  }

  const handleEdit = (document) => {
    setEditingDocument(document)
    setModalOpen(true)
  }

  const handleDelete = (document) => {
    setDocumentToDelete(document)
    setDeleteConfirmOpen(true)
  }

  const confirmDelete = async () => {
    if (!documentToDelete) return

    try {
      const response = await companyDocumentService.delete(documentToDelete.id)
      if (response.success) {
        setDeleteConfirmOpen(false)
        setDocumentToDelete(null)
        setRefreshTrigger(prev => prev + 1) // Forçar reload
      } else {
        alert(response.message || 'Erro ao deletar documento')
      }
    } catch (error) {
      console.error('Erro ao deletar documento:', error)
      alert(error.response?.data?.message || 'Erro ao deletar documento')
    }
  }

  const handleView = (document) => {
    if (document.file_url) {
      window.open(document.file_url, '_blank')
    }
  }

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
    setCurrentPage(1) // Reset para primeira página ao filtrar
  }

  return (
    <div className="docs-page company-documents-page">
      <header className="docs-header">
        <div>
          <h1 className="docs-title">
            Documentos <span className="docs-title-highlight">da Empresa</span>
          </h1>
          <p className="docs-subtitle">Arquivos, validade e categorias</p>
        </div>
        <button
          type="button"
          className="docs-new"
          onClick={() => {
            setEditingDocument(null)
            setModalOpen(true)
          }}
        >
          <Plus size={16} />
          Adicionar documento
        </button>
      </header>

      {expiringDocuments.length > 0 && (
        <div className="company-documents-page-alerts">
          <DocumentExpiryAlert
            documents={expiringDocuments}
            onDocumentClick={handleView}
          />
        </div>
      )}

      <DocumentList
        documents={documents}
        loading={loading}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onPageChange={setCurrentPage}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        filters={{
          showFilters: true,
          categories: categories
        }}
        onFilterChange={handleFilterChange}
      />

      <CompanyDocumentModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false)
          setEditingDocument(null)
        }}
        onSubmit={handleUpload}
        document={editingDocument}
        categories={categories}
      />

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false)
          setDocumentToDelete(null)
        }}
        onConfirm={confirmDelete}
        title="Confirmar Exclusão"
        message={`Deseja realmente excluir o documento "${documentToDelete?.title}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        variant="danger"
      />
    </div>
  )
}

export default CompanyDocumentsPage

